import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { Deck } from '../../types/entities';
import { CommandPalette, useCommandPaletteShortcut, type CommandItem } from '../../components/navigation/CommandPalette';
import { DialogA11y } from '../../components/feedback/DialogA11y';
import { useToast } from '../../components/feedback/ToastProvider';
import { useAuth } from '../providers/AuthProvider';
import { useLibrary } from '../providers/LibraryProvider';
import { useSyncStatus } from '../providers/SyncStatusProvider';

interface NavigationItem {
  readonly to: string;
  readonly label: string;
  readonly icon: string;
  readonly end?: boolean;
  readonly keywords?: string;
}

const navigation: readonly NavigationItem[] = [
  { to: '/app', label: 'Home', icon: 'dashboard', end: true, keywords: 'início hoje plano' },
  { to: '/app/biblioteca', label: 'Minha Biblioteca', icon: 'folder_open', keywords: 'decks pastas cards flashcards' },
  { to: '/app/calendario', label: 'Calendário', icon: 'calendar_month', keywords: 'agenda provas compromissos' },
  { to: '/app/disciplinas', label: 'Minhas Disciplinas', icon: 'collections_bookmark', keywords: 'matérias aulas notas' },
  { to: '/app/questoes', label: 'Banco de Questões', icon: 'quiz', keywords: 'simulado prova erros' },
  { to: '/app/evolucao', label: 'Evolução', icon: 'monitoring', keywords: 'estatísticas progresso' },
  { to: '/app/comunidade', label: 'Comunidades', icon: 'public', keywords: 'amigos código compartilhar' },
];

const secondaryRoutes: readonly NavigationItem[] = [
  { to: '/app/estudar', label: 'Estudar', icon: 'play_circle', keywords: 'sessão revisar' },
  { to: '/app/prova', label: 'Prova', icon: 'assignment', keywords: 'simulado questões' },
  { to: '/app/perfil', label: 'Perfil', icon: 'person', keywords: 'conta nome' },
  { to: '/app/configuracoes', label: 'Configurações', icon: 'settings', keywords: 'tema paleta meta' },
];

const statusLabels = {
  offline: 'Offline',
  saving: 'Salvando...',
  saved: 'Salvo',
  syncing: 'Sincronizando...',
  synced: 'Sincronizado',
  error: 'Erro ao sincronizar',
} as const;

function Icon({ children, className = '' }: { readonly children: string; readonly className?: string }): React.JSX.Element {
  return <span className={`material-symbols-outlined ${className}`} aria-hidden="true">{children}</span>;
}

function sectionLabel(pathname: string): string {
  const match = [...navigation, ...secondaryRoutes].find((item) => (item.end ? pathname === item.to : pathname.startsWith(item.to)));
  return match?.label ?? 'Echo';
}

export function AppShell(): React.JSX.Element {
  const { user, loading } = useAuth();
  const { status, online } = useSyncStatus();
  const { notify } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => window.localStorage.getItem('echo-sidebar-collapsed') === 'true');
  const [mobileOpen, setMobileOpen] = useState(false);
  // Pastas, decks, vencidos e preferências vêm do provider compartilhado (listeners em tempo real):
  // nenhuma consulta extra ao trocar de rota e nenhum evento manual para manter os contadores.
  const { folders, decks, cardCount, dueCount, preferences } = useLibrary();
  const [profileDisplayName, setProfileDisplayName] = useState('');
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useCommandPaletteShortcut(setPaletteOpen);

  // As rotas mais usadas são baixadas em segundo plano logo após o shell montar,
  // para a primeira navegação não esperar o download do chunk.
  useEffect(() => {
    const preload = (): void => {
      void import('../../features/dashboard/DashboardPage');
      void import('../../features/library/LibraryPage');
      void import('../../features/study/StudyPage');
    };
    const handle = window.setTimeout(preload, 1_200);
    return () => window.clearTimeout(handle);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = (): void => {
      root.dataset.echoTheme = preferences?.theme === 'system' ? (media.matches ? 'dark' : 'light') : preferences?.theme ?? 'light';
      root.dataset.echoPalette = preferences?.palette ?? 'cobalt';
      root.dataset.echoCardShape = preferences?.cardShape ?? 'rectangle';
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', root.dataset.echoTheme === 'dark' ? '#14181a' : '#3154d8');
    };
    apply();
    media.addEventListener('change', apply);
    return () => {
      media.removeEventListener('change', apply);
      delete root.dataset.echoTheme;
      delete root.dataset.echoPalette;
      delete root.dataset.echoCardShape;
    };
  }, [preferences]);

  useEffect(() => {
    setMobileOpen(false);
    setNotificationsOpen(false);
    setPaletteOpen(false);
    document.title = `${sectionLabel(location.pathname)} · Echo Study`;
    window.dispatchEvent(new Event('echo-route-changed'));
    document.getElementById('main-content')?.scrollTo?.({ top: 0 });
  }, [location.pathname]);

  useEffect(() => {
    if (!notificationsOpen) return;
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setNotificationsOpen(false);
    };
    const closeOnOutsideClick = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest('#notification-center, .header-notification-btn')) setNotificationsOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    window.addEventListener('mousedown', closeOnOutsideClick);
    return () => {
      window.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('mousedown', closeOnOutsideClick);
    };
  }, [notificationsOpen]);

  // Conectividade e falhas de sincronização viram avisos globais, não texto escondido num canto.
  useEffect(() => {
    if (!online) notify('Você está offline. Pode continuar estudando: tudo será sincronizado ao reconectar.', { tone: 'warning', title: 'Sem conexão', durationMs: 6_000 });
  }, [notify, online]);
  useEffect(() => {
    if (status === 'error') notify('Suas alterações locais estão preservadas. Verifique a conexão e tente novamente.', { tone: 'error', title: 'Falha ao sincronizar' });
  }, [notify, status]);

  useEffect(() => {
    setProfileDisplayName(user?.displayName?.trim() || user?.email?.split('@')[0] || 'Usuário');
    const receiveProfile = (event: Event): void => setProfileDisplayName((event as CustomEvent<string>).detail);
    window.addEventListener('echo-profile-changed', receiveProfile);
    return () => window.removeEventListener('echo-profile-changed', receiveProfile);
  }, [user]);

  const decksByFolder = useMemo(() => {
    const grouped = new Map<string, Deck[]>();
    decks.forEach((deck) => {
      if (!deck.folderId) return;
      grouped.set(deck.folderId, [...(grouped.get(deck.folderId) ?? []), deck]);
    });
    return grouped;
  }, [decks]);

  const folderNames = useMemo(() => new Map(folders.map((folder) => [folder.id, folder.name])), [folders]);

  const go = useCallback((to: string, state?: unknown): void => { void navigate(to, state === undefined ? undefined : { state }); }, [navigate]);

  const commands = useMemo<readonly CommandItem[]>(() => [
    ...[...navigation, ...secondaryRoutes].map<CommandItem>((item) => ({ id: `nav-${item.to}`, label: item.label, icon: item.icon, group: 'Ir para', keywords: item.keywords ?? '', run: () => go(item.to) })),
    { id: 'act-plan', label: 'Iniciar plano do dia', hint: dueCount > 0 ? `${dueCount} card${dueCount === 1 ? '' : 's'} para revisar` : 'Abrir Study', icon: 'play_arrow', group: 'Ações', keywords: 'estudar revisar sessão', run: () => go('/app') },
    { id: 'act-deck', label: 'Novo deck ou card', hint: 'Abre a Biblioteca', icon: 'note_add', group: 'Ações', keywords: 'criar adicionar flashcard', run: () => go('/app/biblioteca', { openNewDeck: true }) },
    { id: 'act-import', label: 'Importar flashcards ou prova', hint: 'CSV, Anki, PDF ou DOCX', icon: 'upload_file', group: 'Ações', keywords: 'importar arquivo', run: () => go('/app/biblioteca', { openImport: true }) },
    { id: 'act-event', label: 'Novo compromisso', hint: 'Prova, aula ou trabalho', icon: 'event', group: 'Ações', keywords: 'calendário agenda', run: () => go('/app/calendario') },
    { id: 'act-theme', label: 'Alternar tema claro/escuro', hint: 'Configurações › Aparência', icon: 'dark_mode', group: 'Ações', keywords: 'dark light', run: () => go('/app/configuracoes') },
    ...decks.map<CommandItem>((deck) => ({ id: `deck-${deck.id}`, label: deck.name, hint: deck.folderId ? folderNames.get(deck.folderId) ?? 'Sem pasta' : 'Sem pasta', icon: 'style', group: 'Decks', keywords: 'deck estudar', run: () => go('/app/estudar', { deckId: deck.id }) })),
  ], [decks, dueCount, folderNames, go]);

  if (loading) return <main className="route-loading fullscreen" role="status"><div className="route-loading-mark" aria-hidden="true"><img src="/favicon.svg" alt="" width="44" height="44" /><span className="route-loading-ring" /></div><p>Verificando sua conta...</p></main>;
  if (!user) return <Navigate to="/entrar" replace state={{ from: `${location.pathname}${location.search}` }} />;

  const displayName = profileDisplayName || user.displayName?.trim() || user.email?.split('@')[0] || 'Usuário';
  const initials = displayName.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('');
  const showLibraryTree = location.pathname.startsWith('/app/biblioteca');
  const activeNavigation = preferences?.uiMode === 'simple'
    ? navigation.filter((item) => ['/app', '/app/biblioteca', '/app/questoes'].includes(item.to))
    : navigation;
  const currentSection = sectionLabel(location.pathname);
  const isMac = /Mac|iPhone|iPad/.test(navigator.platform);

  function toggleSidebar(): void {
    if (window.matchMedia('(max-width: 900px)').matches) {
      setMobileOpen((current) => !current);
      return;
    }
    setCollapsed((current) => {
      window.localStorage.setItem('echo-sidebar-collapsed', String(!current));
      return !current;
    });
  }

  return (
    <div className={`legacy-app-shell app-body${collapsed ? ' sidebar-collapsed' : ''}${mobileOpen ? ' sidebar-mobile-open' : ''}`}>
      <a className="skip-link" href="#main-content">Pular para o conteúdo</a>
      <button className="sidebar-backdrop" type="button" aria-label="Fechar menu" onClick={() => setMobileOpen(false)} />
      <button type="button" className="mobile-menu-fab" aria-label="Abrir menu de navegação" aria-expanded={mobileOpen} onClick={toggleSidebar}><Icon>menu</Icon></button>
      <nav className="mobile-bottom-nav" aria-label="Navegação rápida">
        {activeNavigation.slice(0, 4).map((item) => <NavLink className="mobile-tab-btn" key={`mobile-${item.to}`} to={item.to} {...(item.end ? { end: true } : {})}><Icon>{item.icon}</Icon><span>{item.label === 'Minha Biblioteca' ? 'Biblioteca' : item.label === 'Minhas Disciplinas' ? 'Disciplinas' : item.label}</span></NavLink>)}
        <button className="mobile-tab-btn" type="button" aria-label="Abrir mais opções" aria-expanded={mobileOpen} onClick={toggleSidebar}><Icon>menu</Icon><span>Mais</span></button>
      </nav>

      <aside className="legacy-sidebar">
        <div className="sidebar-top">
          <button className="icon-btn sidebar-toggle" type="button" aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'} aria-pressed={!collapsed} onClick={toggleSidebar}><Icon>{collapsed ? 'menu' : 'menu_open'}</Icon></button>
          <div className="sidebar-brand"><img className="sidebar-app-icon" src="/favicon.svg" alt="" aria-hidden="true" /><div className="sidebar-title">Echo Study</div><div className="sidebar-subtitle">Plataforma de estudo</div></div>
        </div>

        <nav className="side-nav" aria-label="Navegação principal">
          {activeNavigation.map((item) => (
            <NavLink className="side-nav-btn" key={item.to} to={item.to} {...(item.end ? { end: true } : {})} title={collapsed ? item.label : undefined}>
              <Icon className="nav-icn">{item.icon}</Icon><span className="nav-label">{item.label}</span>
              {item.to === '/app' && dueCount > 0 && <span className="nav-badge" aria-label={`${dueCount} cards para revisar`}>{dueCount > 99 ? '99+' : dueCount}</span>}
            </NavLink>
          ))}
        </nav>

        {showLibraryTree && folders.length > 0 && (
          <div className="sidebar-manage-panel">
            <div className="sidebar-manage-title">Pastas e decks</div>
            <div className="sidebar-manage-tree">
              {folders.map((folder) => (
                <div className="sidebar-tree-folder" key={folder.id}>
                  <div className="sidebar-folder-row"><Icon className="folder-chevron">expand_more</Icon><Icon className="folder-icon">folder</Icon><span>{folder.name}</span><small>{decksByFolder.get(folder.id)?.length ?? 0} decks</small></div>
                  {(decksByFolder.get(folder.id) ?? []).map((deck) => <span className="sidebar-deck-row" key={deck.id}><Icon>description</Icon>{deck.name}</span>)}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="sidebar-footer">
          <NavLink className="side-nav-btn" to="/app/configuracoes" title={collapsed ? 'Configurações' : undefined}><Icon className="nav-icn">settings</Icon><span className="nav-label">Configurações</span></NavLink>
        </div>
        <div className="sidebar-auth-section">
          <NavLink className="sidebar-user-info" to="/app/perfil" aria-label="Abrir perfil">
            <span className="sidebar-user-avatar">{initials || <Icon>person</Icon>}</span>
            <span className="sidebar-user-details"><span className="sidebar-user-name-row"><strong>{displayName}</strong></span><small>{user.email}</small></span>
            <Icon className="sidebar-profile-settings">chevron_right</Icon>
          </NavLink>
        </div>
      </aside>

      <div className="legacy-app-container">
        <header className="legacy-app-header">
          <div className="header-brand-row">
            <h1 className="app-title">Echo</h1>
            <span className="header-section" aria-current="page"><Icon>chevron_right</Icon>{currentSection}</span>
          </div>
          <button className="header-search" type="button" onClick={() => setPaletteOpen(true)} aria-label="Buscar telas, decks ou ações" aria-keyshortcuts={isMac ? 'Meta+K' : 'Control+K'}>
            <Icon>search</Icon>
            <span>Buscar ou pular para...</span>
            <kbd>{isMac ? '⌘' : 'Ctrl'} K</kbd>
          </button>
          <div className="header-right" aria-label="Resumo rápido da plataforma">
            <span className="header-chip"><Icon>folder</Icon><span><strong>{decks.length}</strong><small>Decks</small></span></span>
            <span className="header-chip"><Icon>style</Icon><span><strong>{cardCount ?? '—'}</strong><small>Cards</small></span></span>
            <button className="header-chip highlight" type="button" onClick={() => go('/app')} title="Abrir plano do dia"><Icon>schedule</Icon><span><strong>{dueCount}</strong><small>Para revisar</small></span></button>
          </div>
          <span className={`sync-status legacy-sync-status sync-${status}`} role="status" title={statusLabels[status]}><span className="sync-dot" aria-hidden="true" /><span className="sync-label">{statusLabels[status]}</span></span>
          <div className="header-notification-wrap">
            <button className="header-notification-btn" type="button" aria-label="Abrir central de notificações" aria-expanded={notificationsOpen} aria-controls="notification-center" onClick={() => setNotificationsOpen((open) => !open)}><Icon>notifications</Icon>{(dueCount > 0 || status === 'error') && <span className="notification-indicator" aria-hidden="true" />}</button>
            {notificationsOpen && <section className="notification-center" id="notification-center" role="dialog" aria-modal="true" aria-label="Central de notificações">
              <header><div><span>ATUALIZAÇÕES</span><strong>Notificações</strong></div><button type="button" aria-label="Fechar central de notificações" onClick={() => setNotificationsOpen(false)}><Icon>close</Icon></button></header>
              <div className="notification-list">
                {status === 'error' && <article className="notification-item attention"><span><Icon>warning</Icon></span><div><strong>Falha na sincronização</strong><p>Confira sua conexão. Suas alterações locais continuam preservadas.</p></div></article>}
                {dueCount > 0 ? <article className="notification-item due"><span><Icon>schedule</Icon></span><div><strong>{dueCount} card{dueCount === 1 ? '' : 's'} para revisar</strong><p>Uma sessão curta agora ajuda a manter o ritmo.</p><NavLink to="/app/estudar">Revisar agora <Icon>arrow_forward</Icon></NavLink></div></article> : <article className="notification-item success"><span><Icon>check_circle</Icon></span><div><strong>Revisões em dia</strong><p>Nenhum card vencido neste momento.</p></div></article>}
                <article className="notification-item"><span><Icon>library_books</Icon></span><div><strong>Sua biblioteca</strong><p>{decks.length} deck{decks.length === 1 ? '' : 's'} e {cardCount ?? '—'} card{cardCount === 1 ? '' : 's'} disponíveis.</p><NavLink to="/app/biblioteca">Abrir Biblioteca <Icon>arrow_forward</Icon></NavLink></div></article>
              </div>
            </section>}
          </div>
        </header>
        <main className="legacy-route-content" id="main-content" tabIndex={-1}><div className="route-enter" key={location.pathname}><Outlet /></div></main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={commands} />
      <DialogA11y />
    </div>
  );
}
